import React from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { Button, FormHelperText, Grid, TextField } from "@mui/material";

import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseCheckbox from "components/Shared/Base/BaseCheckbox";
import BaseSelect from "components/Shared/Base/BaseSelect";

import { useLazyGetDDLAuthorizationListQuery } from "store/services/authorizationApi";
import { useLazyGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";

import { useAuth } from "hooks/useAuth";

import { USER_TYPES } from "data/static/staticOptions";

const INITIAL_STATE = {
  name: "",
  email: "",
  authorizations: [],
  defaultAuthorization: "",
  phone: "",
  designation: "",
  typeRefSource: "System",
  autoGeneratePassword: true,
  password: "",
  confirmPassword: "",
};

const UserForm = ({
  userDetail = null,
  isBusy = false,
  onSubmit = () => {},
}) => {
  const [defaultRoleOptions, setDefaultRoleOptions] = React.useState([]);

  const { viewEntity, isSystemAdmin, isEntityAdmin } = useAuth();

  const navigate = useNavigate();

  // react form hook
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm({
    defaultValues: INITIAL_STATE,
  });

  // RTK Query
  const [
    fetchEntities,
    {
      data: entities = [],
      isLoading: loadingEntities,
      isFetching: fetchingEntities,
    },
  ] = useLazyGetDDLEntityListQuery();

  const [
    fetchAccounts,
    {
      data: accounts = [],
      isLoading: loadingAccounts,
      isFetching: fetchingAccounts,
    },
  ] = useLazyGetDDLAccountListQuery();

  const [
    fetchAuthorizations,
    {
      data: authorizationList = [],
      isLoading: loadingAuthorizations,
      isFetching: fetchingAuthorizations,
    },
  ] = useLazyGetDDLAuthorizationListQuery();

  const _mappedAccounts = React.useMemo(
    () =>
      accounts?.map((a) => ({
        label: a.name,
        id: a._id,
      })),
    [accounts]
  );

  const _mappedEntities = React.useMemo(
    () =>
      entities?.map((a) => ({
        label: a.name,
        id: a._id,
      })) ?? [],
    [entities]
  );

  const _mappedAuthorizations = React.useMemo(
    () =>
      authorizationList?.map((a) => ({
        label: a.role,
        id: a._id,
      })) ?? [],
    [authorizationList]
  );

  const selectedAccount = watch("account");

  const selectedUserType = watch("typeRefSource");

  const filteredAuthorizations = React.useMemo(() => {
    if (["Referral"].includes(selectedUserType)) {
      setValue("authorizations", []);
      setValue("defaultAuthorization", "");
      return _mappedAuthorizations?.filter((el) => el?.label === "Entity User");
    }

    return _mappedAuthorizations;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_mappedAuthorizations, selectedUserType]);

  // methods
  const handleOnUserSubmit = (data) => {
    const payload = {
      ...data,
      authorizations: data?.authorizations?.map((el) => el?.id),
      defaultAuthorization: data?.defaultAuthorization?.id,
      entity: data?.entity?.id,
    };

    delete payload.account;

    if (!payload.typeRefSource || payload.typeRefSource === "System")
      delete payload.typeRefSource;

    if (userDetail) {
      delete payload.authorizations;
      delete payload.defaultAuthorization;
      delete payload.entity;
      delete payload.typeRefSource;
    }

    onSubmit(payload);
  };

  React.useEffect(() => {
    if (!accounts?.length || !viewEntity) return;

    if (!isSystemAdmin)
      setValue("account", {
        label: viewEntity?.entity?.account?.name,
        id: viewEntity?.entity?.account?._id,
      });

    if (isEntityAdmin)
      setValue("entity", {
        label: viewEntity?.entity?.name,
        id: viewEntity?.entity?._id,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, viewEntity, isSystemAdmin, isEntityAdmin]);

  React.useEffect(() => {
    if (!selectedAccount) return;

    fetchEntities({ account: selectedAccount?.id });
  }, [fetchEntities, selectedAccount]);

  React.useEffect(() => {
    if (!viewEntity?.authorization) return;

    let levels = [];
    if (viewEntity?.authorization?.level === "Entity") levels = ["Entity"];
    if (viewEntity?.authorization?.level === "Account")
      levels = ["Entity", "Account"];

    fetchAuthorizations({ levels });
  }, [fetchAuthorizations, viewEntity?.authorization]);

  React.useEffect(() => {
    if (!userDetail) return fetchAccounts();

    reset({
      name: userDetail?.name,
      email: userDetail?.email,
      phone: userDetail?.phone,
      designation: userDetail?.designation,
      typeRefSource: userDetail?.typeRefSource ?? "System",
    });
  }, [fetchAccounts, reset, userDetail]);

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={5}>
          {!userDetail && (
            <>
              {!isEntityAdmin && (
                <Grid item xs={6}>
                  <Controller
                    name="account"
                    control={control}
                    rules={{ required: "Please select an Account." }}
                    render={({ field }) => (
                      <BaseAutocomplete
                        required
                        fullWidth
                        label="Account"
                        value={field?.value ?? ""}
                        options={!fetchingAccounts ? _mappedAccounts : []}
                        onChange={(_, account) => {
                          setValue("account", account);
                          setValue("entity", null);
                        }}
                        loading={loadingAccounts || fetchingAccounts}
                        loadingText="Loading Accounts..."
                        readOnly={!isSystemAdmin}
                      />
                    )}
                  />
                  {!!errors.account && (
                    <FormHelperText error>
                      {errors.account.message}
                    </FormHelperText>
                  )}
                </Grid>
              )}

              <Grid item xs={6}>
                <Controller
                  name="entity"
                  control={control}
                  rules={{ required: "Please select an Entity." }}
                  render={({ field }) => (
                    <BaseAutocomplete
                      required
                      fullWidth
                      label="Entity"
                      value={field?.value ?? ""}
                      options={!fetchingEntities ? _mappedEntities : []}
                      onChange={(_, entity) => {
                        setValue("entity", entity);
                      }}
                      loading={loadingEntities || fetchingEntities}
                      loadingText="Loading Entities..."
                      readOnly={isEntityAdmin}
                      error={!!errors?.entity}
                      helperText={errors?.entity?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <Controller
                  name="typeRefSource"
                  control={control}
                  render={({ field }) => (
                    <BaseSelect
                      {...field}
                      fullWidth
                      label="Type"
                      options={USER_TYPES?.filter(
                        (type) => !["Client", "College"].includes(type)
                      )}
                    />
                  )}
                />
              </Grid>

              {!isEntityAdmin && <Grid item xs={6} />}
            </>
          )}

          <Grid item xs={6}>
            <Controller
              name="name"
              control={control}
              rules={{
                required: "Please provide a Name.",
                maxLength: { value: 100, message: "Max 100 characters." },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  label="Name"
                  type="text"
                  size="small"
                  placeholder="Enter name"
                  error={!!errors?.name}
                  helperText={errors?.name?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="email"
              control={control}
              rules={{
                required: "Please provide a Name.",
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  required
                  label="Email"
                  type="text"
                  size="small"
                  placeholder="Enter name"
                  error={!!errors?.email}
                  helperText={errors?.email?.message}
                />
              )}
            />
          </Grid>

          {!["Referral"].includes(selectedUserType) && (
            <>
              <Grid item xs={6}>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Phone"
                      placeholder="Enter Contact Number"
                      size="small"
                      type="tel"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <Controller
                  name="designation"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Designation"
                      placeholder="Enter User Designation"
                      size="small"
                    />
                  )}
                />
              </Grid>
            </>
          )}

          {!userDetail && (
            <>
              <Grid item xs={6}>
                <Controller
                  name="authorizations"
                  control={control}
                  rules={{ required: "Please select Authorizations." }}
                  render={({ field }) => (
                    <BaseAutocomplete
                      required
                      fullWidth
                      size="small"
                      multiple
                      label="Authorizations"
                      value={field?.value ?? []}
                      options={filteredAuthorizations}
                      onChange={(_, authorizations) => {
                        setValue("defaultAuthorization", "");
                        setValue("authorizations", authorizations);
                        setDefaultRoleOptions(authorizations);
                      }}
                      loading={loadingAuthorizations || fetchingAuthorizations}
                      loadingText="Loading Authorizations..."
                      limitTags={2}
                      filterSelectedOptions
                      error={!!errors?.authorizations}
                      helperText={errors?.authorizations?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <Controller
                  name="defaultAuthorization"
                  control={control}
                  rules={{ required: "Please select a Default Authorization." }}
                  render={({ field }) => (
                    <BaseAutocomplete
                      required
                      fullWidth
                      size="small"
                      label="Default Authorization"
                      value={field?.value ?? ""}
                      options={defaultRoleOptions}
                      onChange={(_, defaultAuthorization) => {
                        setValue("defaultAuthorization", defaultAuthorization);
                      }}
                      error={!!errors?.defaultAuthorization}
                      helperText={errors?.defaultAuthorization?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="autoGeneratePassword"
                  control={control}
                  render={({ field }) => (
                    <BaseCheckbox {...field} label="Auto Generate Password" />
                  )}
                />
              </Grid>

              {!watch("autoGeneratePassword") && (
                <>
                  <Grid item xs={6}>
                    <TextField
                      {...register("password", {
                        required: "Please provide a Password.",
                      })}
                      fullWidth
                      label="Password"
                      type="password"
                      size="small"
                      placeholder="Enter password"
                      error={!!errors?.password}
                      helperText={errors?.password?.message}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      {...register("confirmPassword", {
                        required: "Please provide Confirm Password.",
                      })}
                      fullWidth
                      label="Confirm Password"
                      type="password"
                      size="small"
                      placeholder="Confirm password"
                      error={!!errors?.confirmPassword}
                      helperText={errors?.confirmPassword?.message}
                    />
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>

        <Button variant="contained" onClick={handleSubmit(handleOnUserSubmit)}>
          {userDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default UserForm;
